import { useState, useCallback, useMemo } from 'react';
import { generateId, parseLogLine } from '../utils/helpers';
import { Filter } from '../utils/filters';

export const useLogInspector = () => {
  const [rawLogs, setRawLogs] = useState([]);
  const [containers, setContainers] = useState(new Set());
  const [tabs, setTabs] = useState([
    {
      id: generateId(),
      name: 'Main View',
      baseFilters: Filter.empty().toObject(),
      filters: Filter.empty().toObject()
    }
  ]);
  const [activeTabId, setActiveTabId] = useState(tabs[0].id);
  const [bookmarks, setBookmarks] = useState([]);
  const [showContainers, setShowContainers] = useState(true);
  const [containerDropdownOpen, setContainerDropdownOpen] = useState(false);
  const [timestampMode, setTimestampMode] = useState('full'); // 'full', 'time-only', 'hidden'

  const activeTab = useMemo(() => 
    tabs.find(t => t.id === activeTabId),
    [tabs, activeTabId]
  );

  // Process imported logs from text
  const processImport = useCallback((text) => {
    if (!text.trim()) return;

    const lines = text.split(/\r?\n/);
    const newRawLogs = [];
    const newContainers = new Set();

    lines.forEach((line, index) => {
      if (!line.trim()) return;
      const parsed = parseLogLine(line, index);
      if (parsed) {
        newRawLogs.push(parsed);
        newContainers.add(parsed.container);
      }
    });

    setRawLogs(newRawLogs);
    setContainers(newContainers);

    // Reset tabs with new containers
    setTabs(prevTabs => prevTabs.map(t => ({
      ...t,
      baseFilters: {
        ...t.baseFilters,
        containers: Array.from(newContainers)
      },
      filters: {
        ...t.filters,
        containers: Array.from(newContainers)
      }
    })));

    return newRawLogs.length;
  }, []);

  // Process imported log files (from zip or individual files)
  const processImportFiles = useCallback((logFiles) => {
    if (!logFiles || logFiles.length === 0) return;

    // Merge all logs from all files
    const allLogs = [];
    const newContainers = new Set();

    logFiles.forEach(file => {
      file.logs.forEach(log => {
        allLogs.push(log);
        newContainers.add(log.container);
      });
    });

    // Sort all logs by timestamp
    allLogs.sort((a, b) => {
      // Handle logs without timestamps (put them at the end)
      if (!a.tsValue && !b.tsValue) return 0;
      if (!a.tsValue) return 1;
      if (!b.tsValue) return -1;
      return a.tsValue - b.tsValue;
    });

    // Renumber logs after sorting
    allLogs.forEach((log, index) => {
      log.id = index;
    });

    setRawLogs(allLogs);
    setContainers(newContainers);

    // Reset tabs with new containers - keep existing tabs structure
    setTabs(prevTabs => prevTabs.map(t => ({
      ...t,
      baseFilters: {
        ...t.baseFilters,
        containers: Array.from(newContainers)
      },
      filters: {
        ...t.filters,
        containers: Array.from(newContainers)
      }
    })));

    return allLogs.length;
  }, []);

  // Tab management
  const addTab = useCallback((name = null) => {
    const id = generateId();
    const tabName = name || `View ${tabs.length + 1}`;

    const newTab = {
      id,
      name: tabName,
      baseFilters: Filter.empty(Array.from(containers)).toObject(),
      filters: Filter.empty(Array.from(containers)).toObject()
    };

    setTabs(prev => [...prev, newTab]);
    setActiveTabId(id);
  }, [tabs.length, containers]);

  const addTabFromCurrentFilter = useCallback(() => {
    if (!activeTab) return;

    const id = generateId();
    const tabName = `Filter ${tabs.length + 1}`;

    // Combine base filters with current filters using Filter class
    const baseFilter = Filter.fromObject(activeTab.baseFilters);
    const currentFilter = Filter.fromObject(activeTab.filters);
    const combinedFilter = baseFilter.combine(currentFilter);

    const newTab = {
      id,
      name: tabName,
      baseFilters: combinedFilter.toObject(),
      filters: Filter.empty(combinedFilter.containers).toObject()
    };

    setTabs(prev => [...prev, newTab]);
    setActiveTabId(id);
  }, [activeTab, tabs.length]);

  const closeTab = useCallback((id) => {
    if (tabs.length === 1) return;

    setTabs(prev => {
      const newTabs = prev.filter(t => t.id !== id);
      if (activeTabId === id) {
        setActiveTabId(newTabs[0].id);
      }
      return newTabs;
    });
  }, [tabs.length, activeTabId]);

  const switchTab = useCallback((id) => {
    setActiveTabId(id);
  }, []);

  // Filter management
  const updateFilter = useCallback((type, value) => {
    setTabs(prev => prev.map(t => 
      t.id === activeTabId
        ? { ...t, filters: { ...t.filters, [type]: value } }
        : t
    ));
  }, [activeTabId]);

  const toggleContainer = useCallback((container) => {
    setTabs(prev => prev.map(t => {
      if (t.id !== activeTabId) return t;
      
      const currentContainers = t.filters.containers;
      const newContainers = currentContainers.includes(container)
        ? currentContainers.filter(c => c !== container)
        : [...currentContainers, container];
      
      return { ...t, filters: { ...t.filters, containers: newContainers } };
    }));
  }, [activeTabId]);

  const toggleAllContainers = useCallback((select) => {
    setTabs(prev => prev.map(t => 
      t.id === activeTabId
        ? { ...t, filters: { ...t.filters, containers: select ? Array.from(containers) : [] } }
        : t
    ));
  }, [activeTabId, containers]);

  // Bookmark management
  const toggleBookmark = useCallback((logId, note = '') => {
    setBookmarks(prev => {
      const existing = prev.find(b => b.logId === logId);
      
      if (existing) {
        return prev.filter(b => b.logId !== logId);
      } else {
        const logEntry = rawLogs[logId];
        const newBookmark = {
          id: generateId(),
          logId,
          note,
          message: logEntry.message,
          timestamp: logEntry.timestamp,
          container: logEntry.container
        };
        return [...prev, newBookmark].sort((a, b) => a.logId - b.logId);
      }
    });
  }, [rawLogs]);

  const isBookmarked = useCallback((logId) => {
    return bookmarks.some(b => b.logId === logId);
  }, [bookmarks]);

  const updateBookmarkNote = useCallback((logId, newNote) => {
    setBookmarks(prev => prev.map(b =>
      b.logId === logId ? { ...b, note: newNote } : b
    ));
  }, []);

  const removeBookmark = useCallback((logId) => {
    setBookmarks(prev => prev.filter(b => b.logId !== logId));
  }, []);

  // Filtered logs based on active tab
  const filteredLogs = useMemo(() => {
    if (!activeTab) return [];

    const baseFilter = Filter.fromObject(activeTab.baseFilters);
    const currentFilter = Filter.fromObject(activeTab.filters);

    return rawLogs.filter(log => {
      // Apply base filter first
      if (!baseFilter.apply(log)) return false;
      
      // Then apply current filter
      if (!currentFilter.apply(log)) return false;
      
      return true;
    });
  }, [rawLogs, activeTab]);

  return {
    // State
    rawLogs,
    containers,
    tabs,
    activeTabId,
    activeTab,
    bookmarks,
    showContainers,
    containerDropdownOpen,
    timestampMode,
    filteredLogs,

    // Actions
    processImport,
    processImportFiles,
    addTab,
    addTabFromCurrentFilter,
    closeTab,
    switchTab,
    updateFilter,
    toggleContainer,
    toggleAllContainers,
    toggleBookmark,
    isBookmarked,
    updateBookmarkNote,
    removeBookmark,
    setShowContainers,
    setContainerDropdownOpen,
    setTimestampMode
  };
};

